import { Link } from "react-router-dom";
import { useTranslation } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import {
  Camera,
  Aperture,
  PersonSimple,
  Mountains,
  DeviceMobile,
  Lightbulb,
  Sun,
  Backpack,
  ClockCounterClockwise,
  Briefcase,
  GraduationCap,
  Wrench,
  FolderOpen,
  ChartBar,
  CircleDashed,
  GearSix,
  FilmStrip,
  FileText,
  Square,
  TextAa,
  Monitor,
  Plug,
  CaretRight,
  Sparkle,
  Globe,
  CheckCircle,
  BookOpen,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import GaleriaSocialCard from "@/components/GaleriaSocialCard";
import { CameraTypesBadge } from "@/components/CameraTypesBadge";
import { CameraTypesFaq } from "@/components/CameraTypesFaq";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.04 } }),
};

const COPY = {
  en: {
    eyebrow: "DSLR ADVISOR",
    title: "Master your DSLR, Mirrorless or Film camera. Free curriculum. Free tools.",
    badge: "Works with DSLR · Mirrorless · Film",
    sub: "A bilingual photography classroom in a box — manual settings advisor, gear inventory, 20+ in-depth lessons, interactive tools, and printable PDF worksheets. Built for students, hobbyists, and teachers.",
    cta_signup: "Create free account",
    cta_signin: "Sign in",
    preview_eyebrow: "INTERACTIVE CALCULATOR",
    preview_title: "Get a strong starting point for ANY scene",
    preview_sub: "Tell us your camera, lens, lighting, and what you're shooting. We'll recommend Aperture, Shutter Speed, and ISO — calibrated for 75+ DSLR/mirrorless bodies.",
    preview_locked: "Sign in to use the calculator",
    inside_eyebrow: "WHAT'S INSIDE",
    inside_title: "Everything you need to learn manual photography",
    inside_sub: "40+ features, 100% free. Bilingual English/Spanish. Works on phone, tablet, and desktop.",
    closing_title: "Ready to take control of your camera?",
    closing_sub: "Create a free account in 30 seconds. No credit card. No spam.",
    closing_cta: "Get started — it's free",
    footer_note: "Built by a working photographer for the next generation of shooters.",
  },
  es: {
    eyebrow: "DSLR ADVISOR",
    title: "Domina tu cámara DSLR, Mirrorless o de Película. Currículum gratis. Herramientas gratis.",
    badge: "Para DSLR · Mirrorless · Película",
    sub: "Un salón de fotografía bilingüe en una caja — asesor de ajustes manuales, inventario de equipo, 20+ lecciones profundas, herramientas interactivas y hojas PDF imprimibles. Hecho para estudiantes, aficionados y maestros.",
    cta_signup: "Crear cuenta gratis",
    cta_signin: "Iniciar sesión",
    preview_eyebrow: "CALCULADORA INTERACTIVA",
    preview_title: "Obtén un punto de partida sólido para CUALQUIER escena",
    preview_sub: "Dinos tu cámara, lente, iluminación y lo que vas a fotografiar. Te recomendaremos Apertura, Obturación e ISO — calibrado para 75+ cuerpos DSLR/mirrorless.",
    preview_locked: "Inicia sesión para usar la calculadora",
    inside_eyebrow: "QUÉ INCLUYE",
    inside_title: "Todo lo que necesitas para aprender fotografía manual",
    inside_sub: "40+ funciones, 100% gratis. Bilingüe inglés/español. Funciona en teléfono, tablet y escritorio.",
    closing_title: "¿Listo para tomar control de tu cámara?",
    closing_sub: "Crea una cuenta gratis en 30 segundos. Sin tarjeta de crédito. Sin spam.",
    closing_cta: "Empezar — es gratis",
    footer_note: "Hecho por un fotógrafo en activo para la siguiente generación de fotógrafos.",
  },
};

const FEATURES_EN = [
  { Icon: Aperture, title: "Manual Settings Advisor", desc: "Recommendations for Aperture, Shutter Speed & ISO for 75+ cameras based on scene, light, and subject." },
  { Icon: GearSix, title: "Mode Dial Symbol Guide", desc: "Every M, Av, Tv, P, A-DEP, scene and auto symbol decoded — universal Canon/Nikon/Sony/Pentax coverage." },
  { Icon: Aperture, title: "Light Meter Lesson", desc: "Why pros still carry a handheld meter, incident vs reflected, flash + ambient workflows, and reading the meter into camera settings." },
  { Icon: Backpack, title: "My Gear Inventory", desc: "Save cameras, lenses, lighting, support gear. Export as Excel. Personalized recommendations from your own kit." },
  { Icon: PersonSimple, title: "Posing Guide", desc: "20 essential portrait poses across Standing, Seated, Couples, Groups, and Creative — with diagrams + reference photos." },
  { Icon: DeviceMobile, title: "Smartphone Photography", desc: "6 lessons + 10 practice projects + Phone vs DSLR comparison. iOS and Android steps side by side." },
  { Icon: Lightbulb, title: "Lighting Techniques", desc: "1-light to 4-light setups with diagrams. Natural, strobe, continuous, mixed light. Studio + on-location." },
  { Icon: Sun, title: "Golden Hour Calculator", desc: "Sunrise, sunset, blue hour, civil/nautical/astronomical twilight for any location worldwide. Moon phases too." },
  { Icon: Mountains, title: "20+ Lesson Pages", desc: "Histogram, Metering, Exposure Comp, Crop Factor, Filters, Lens Compression, Focus, White Balance, Gray Card, Maintenance, and more." },
  { Icon: GraduationCap, title: "Photography Glossary", desc: "200+ terms defined. Bilingual. Searchable. From 'aperture' to 'zone system'." },
  { Icon: Wrench, title: "Interactive Tools", desc: "DOF calculator, Sunny 16 reference, Handheld speed, Budget calculator, Print size calculator, Shopping list." },
  { Icon: FolderOpen, title: "Photography Projects", desc: "Step-by-step shooting assignments to build your portfolio. Beginner to advanced." },
  { Icon: Briefcase, title: "Photo Career Paths", desc: "10+ photography careers with salaries, gear requirements, training, and pros/cons." },
  { Icon: FileText, title: "Contracts & Forms", desc: "Photography Services Agreement and Model Release — professional legal templates, downloadable PDF." },
  { Icon: Square, title: "Gray Card Lesson", desc: "The cheapest, most under-used pro tool. Workflow for white balance and exposure precision." },
  { Icon: Monitor, title: "Monitor Calibration", desc: "Color spaces, calibration targets, hardware vs software methods, live test patterns you can run on-screen." },
  { Icon: Plug, title: "Tethering Guide", desc: "Wired and wireless tethering setups: Camera Connect, SnapBridge, Tether Tools Air Direct, CamRanger 2." },
  { Icon: ChartBar, title: "Histogram Reading", desc: "Read shadows, midtones, highlights. Know when your exposure is right BEFORE you check the LCD." },
  { Icon: CircleDashed, title: "Filters Guide", desc: "UV, Polarizer, Neutral Density, Graduated ND — what each does, when to use, what to buy first." },
  { Icon: FilmStrip, title: "Studio Setup Guide", desc: "Plan a studio from scratch. Space, lighting kit, modifiers, backgrounds, and starter checklists." },
  { Icon: ClockCounterClockwise, title: "History + PDF Worksheets", desc: "Every calculation saved. Printable PDF worksheets per shoot for offline use." },
  { Icon: TextAa, title: "200+ Quiz Questions", desc: "Test your knowledge across 12 photography topics. Track your score." },
  { Icon: Globe, title: "Bilingual EN/ES", desc: "Every page, lesson, tool, and form translated. Toggle anytime with one click." },
];

const FEATURES_ES = [
  { Icon: Aperture, title: "Asesor de Ajustes Manuales", desc: "Recomendaciones de Apertura, Obturación e ISO para 75+ cámaras según escena, luz y sujeto." },
  { Icon: GearSix, title: "Guía de Símbolos del Dial", desc: "Cada símbolo M, Av, Tv, P, A-DEP, escena y auto decodificado — cobertura universal Canon/Nikon/Sony/Pentax." },
  { Icon: Aperture, title: "Lección de Fotómetro", desc: "Por qué los pros aún llevan fotómetro de mano, incidente vs reflejada, flujos de flash + ambiente, y lectura de fotómetro a ajustes de cámara." },
  { Icon: Backpack, title: "Mi Inventario de Equipo", desc: "Guarda cámaras, lentes, iluminación, soporte. Exporta como Excel. Recomendaciones personalizadas desde tu kit." },
  { Icon: PersonSimple, title: "Guía de Poses", desc: "20 poses esenciales de retrato — De Pie, Sentado, Parejas, Grupos y Creativo — con diagramas + fotos." },
  { Icon: DeviceMobile, title: "Fotografía con Smartphone", desc: "6 lecciones + 10 proyectos prácticos + comparativa Teléfono vs DSLR. Pasos iOS y Android lado a lado." },
  { Icon: Lightbulb, title: "Técnicas de Iluminación", desc: "Configuraciones de 1 a 4 luces con diagramas. Natural, strobe, continua, mixta. Estudio y en locación." },
  { Icon: Sun, title: "Calculadora Hora Dorada", desc: "Salida y puesta del sol, hora azul, crepúsculo civil/nautico/astronomico para cualquier lugar. También fases lunares." },
  { Icon: Mountains, title: "20+ Páginas de Lecciones", desc: "Histograma, Medición, Compensación EV, Factor Recorte, Filtros, Compresión, Enfoque, BB, Tarjeta Gris, Mantenimiento, y más." },
  { Icon: GraduationCap, title: "Glosario de Fotografía", desc: "200+ términos definidos. Bilingüe. Buscable. Desde 'apertura' hasta 'sistema zonal'." },
  { Icon: Wrench, title: "Herramientas Interactivas", desc: "Calc. PDC, Sunny 16, Velocidad a mano, Calc. presupuesto, Calc. tamaño de impresión, Lista de compras." },
  { Icon: FolderOpen, title: "Proyectos Fotograficos", desc: "Tareas paso a paso para construir tu portafolio. Principiante a avanzado." },
  { Icon: Briefcase, title: "Carreras Fotograficas", desc: "10+ carreras de fotografía con salarios, requisitos de equipo, formación y pros/contras." },
  { Icon: FileText, title: "Contratos y Formularios", desc: "Acuerdo de Servicios Fotográficos y Cesión de Imagen — plantillas legales profesionales, PDF descargable." },
  { Icon: Square, title: "Lección de Tarjeta Gris", desc: "La herramienta pro más barata y subutilizada. Flujo para balance de blancos y precisión de exposición." },
  { Icon: Monitor, title: "Calibración del Monitor", desc: "Espacios de color, objetivos, hardware vs software, patrones de prueba en vivo en pantalla." },
  { Icon: Plug, title: "Guía de Tethering", desc: "Configuraciones con cable e inalámbricas: Camera Connect, SnapBridge, Tether Tools Air Direct, CamRanger 2." },
  { Icon: ChartBar, title: "Lectura de Histograma", desc: "Lee sombras, medios tonos, luces. Sabe si la exposición está bien ANTES de mirar la LCD." },
  { Icon: CircleDashed, title: "Guía de Filtros", desc: "UV, Polarizador, ND, ND Graduado — que hace cada uno, cuando usarlo, cual comprar primero." },
  { Icon: FilmStrip, title: "Guía de Estudio", desc: "Planea un estudio desde cero. Espacio, kit de luces, modificadores, fondos y listas de inicio." },
  { Icon: ClockCounterClockwise, title: "Historial + Hojas PDF", desc: "Cada cálculo guardado. Hojas PDF imprimibles por sesión para uso offline." },
  { Icon: TextAa, title: "200+ Preguntas de Quiz", desc: "Prueba tus conocimientos en 12 temas. Lleva tu puntuación." },
  { Icon: Globe, title: "Bilingüe EN/ES", desc: "Cada página, lección, herramienta y formulario traducido. Cambia con un clic." },
];

// Calculator preview — visual recreation of the dashboard form, locked behind login.
function CalculatorPreview({ c }) {
  return (
    <div className="relative bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#F5A524]/5 via-transparent to-transparent pointer-events-none" />
      <div className="p-6 sm:p-8 relative">
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Left — mock form */}
          <div className="space-y-4" aria-hidden="true">
            <div>
              <p className="text-[#71717A] text-xs uppercase tracking-widest mb-1.5">Camera</p>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-[#D4D4D8] text-sm">
                Canon EOS Rebel T3i
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[#71717A] text-xs uppercase tracking-widest mb-1.5">Focal Length</p>
                <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-[#D4D4D8] text-sm">50mm</div>
              </div>
              <div>
                <p className="text-[#71717A] text-xs uppercase tracking-widest mb-1.5">Max Aperture</p>
                <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-[#D4D4D8] text-sm">f/1.8</div>
              </div>
            </div>
            <div>
              <p className="text-[#71717A] text-xs uppercase tracking-widest mb-1.5">Scene</p>
              <div className="flex gap-2 flex-wrap">
                {["Portrait", "Golden Hour", "Outdoor"].map((s) => (
                  <span key={s} className="bg-[#F5A524]/10 border border-[#F5A524]/30 text-[#F5A524] px-2 py-1 rounded text-xs font-medium">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right — mock result */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#121214] border border-[#F5A524]/30 rounded-xl p-5 space-y-3" aria-hidden="true">
            <p className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold">Recommended settings</p>
            <div className="space-y-2">
              {[
                { k: "Aperture", v: "f/2.0", note: "Soft background blur" },
                { k: "Shutter Speed", v: "1/200", note: "Sharp handheld" },
                { k: "ISO", v: "200", note: "Clean, no grain" },
              ].map((r) => (
                <div key={r.k} className="flex items-baseline justify-between border-b border-white/5 pb-1.5 last:border-0">
                  <span className="text-[#A1A1AA] text-xs">{r.k}</span>
                  <span className="text-white font-['Outfit'] font-semibold text-lg">{r.v}</span>
                </div>
              ))}
            </div>
            <p className="text-[#71717A] text-xs italic pt-1">"Golden hour portraits with a fast prime — perfect for soft, dreamy results."</p>
          </div>
        </div>

        {/* Sign-in lock overlay strip */}
        <div className="mt-6 bg-gradient-to-r from-[#F5A524]/15 via-[#F5A524]/10 to-[#F5A524]/15 border border-[#F5A524]/30 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[#F5A524] text-sm font-medium">
            <CheckCircle weight="fill" className="w-4 h-4" />
            {c.preview_locked}
          </div>
          <Link to="/login">
            <Button size="sm" className="bg-[#F5A524] hover:bg-[#F5B041] text-[#0A0A0A] font-medium" data-testid="landing-calc-cta">
              {c.cta_signup}
              <CaretRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { lang } = useTranslation();
  const c = COPY[lang] || COPY.en;
  const features = lang === "es" ? FEATURES_ES : FEATURES_EN;

  return (
    <div className="relative" data-testid="landing-page">
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <motion.div initial="hidden" animate="show" className="text-center max-w-3xl mx-auto">
          <motion.p variants={fadeUp} custom={0} className="text-[#F5A524] text-xs sm:text-sm uppercase tracking-[0.25em] font-semibold mb-4">
            {c.eyebrow}
          </motion.p>
          <motion.div variants={fadeUp} custom={0.5} className="flex justify-center mb-6">
            <CameraTypesBadge />
          </motion.div>
          <motion.h1 variants={fadeUp} custom={1} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight tracking-tight mb-6">
            {c.title}
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="text-[#A1A1AA] text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            {c.sub}
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/login">
              <Button size="lg" className="bg-[#F5A524] hover:bg-[#F5B041] text-[#0A0A0A] font-medium text-base px-6 h-12" data-testid="landing-hero-signup">
                <Camera weight="fill" className="w-5 h-5 mr-2" />
                {c.cta_signup}
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="ghost" className="text-[#A1A1AA] hover:text-white hover:bg-white/5 font-medium text-base px-6 h-12" data-testid="landing-hero-signin">
                {c.cta_signin}
                <CaretRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </motion.div>

          {/* FREE BOOKLET MAGNET */}
          <motion.div variants={fadeUp} custom={4} className="mt-10">
            <Link to="/login" className="block group" data-testid="landing-booklet-magnet">
              <div className="mx-auto max-w-xl flex items-center gap-4 text-left bg-gradient-to-r from-[#F5A524]/15 to-transparent border border-[#F5A524]/30 hover:border-[#F5A524]/60 rounded-2xl p-4 sm:p-5 transition-colors">
                <span className="w-12 h-12 rounded-xl bg-[#F5A524]/20 flex items-center justify-center shrink-0">
                  <BookOpen weight="fill" className="w-7 h-7 text-[#F5A524]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A] bg-[#F5A524] rounded px-1.5 py-0.5">{lang === "es" ? "Gratis" : "Free"}</span>
                    <h3 className="font-['Outfit'] text-white font-semibold text-base leading-snug truncate">
                      {lang === "es" ? "Guía de Campo DSLR — 46 hojas" : "The 46-page DSLR Field Guide"}
                    </h3>
                  </div>
                  <p className="text-[#A1A1AA] text-sm leading-snug">
                    {lang === "es"
                      ? "Crea tu cuenta gratis y descarga el folleto imprimible completo."
                      : "Create your free account and download the complete printable booklet."}
                  </p>
                </div>
                <CaretRight className="w-5 h-5 text-[#F5A524] shrink-0 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* CALCULATOR PREVIEW */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }} className="text-center mb-10">
          <p className="text-[#F5A524] text-xs uppercase tracking-[0.25em] font-semibold mb-3">{c.preview_eyebrow}</p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-3 max-w-3xl mx-auto">
            {c.preview_title}
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">{c.preview_sub}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5, delay: 0.1 }}>
          <CalculatorPreview c={c} />
        </motion.div>
      </section>

      {/* WHAT'S INSIDE — feature grid */}
      <section id="whats-inside" className="bg-gradient-to-b from-[#0A0A0A] via-[#0F0F12] to-[#0A0A0A] border-y border-white/5 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }} className="text-center mb-12">
            <p className="text-[#F5A524] text-xs uppercase tracking-[0.25em] font-semibold mb-3">{c.inside_eyebrow}</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-3 max-w-3xl mx-auto">
              {c.inside_title}
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">{c.inside_sub}</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-features-grid">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.3, delay: (i % 6) * 0.04 }}
                className="bg-[#121214]/80 backdrop-blur-md border border-white/10 hover:border-[#F5A524]/40 rounded-xl p-5 transition-colors group"
                data-testid={`landing-feature-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F5A524]/10 border border-[#F5A524]/20 flex items-center justify-center shrink-0 group-hover:bg-[#F5A524]/20 transition-colors">
                    <f.Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Outfit'] text-white font-semibold text-base mb-1 leading-snug">{f.title}</h3>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GALERÍA SOCIAL CROSS-PROMO */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <GaleriaSocialCard />
      </section>

      {/* CAMERA TYPES FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <CameraTypesFaq />
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }}>
          <Sparkle weight="fill" className="w-8 h-8 text-[#F5A524] mx-auto mb-4" />
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-3">{c.closing_title}</h2>
          <p className="text-[#A1A1AA] mb-4">{c.closing_sub}</p>
          <p className="inline-flex items-center gap-2 text-[#F5A524] text-sm font-medium mb-6 mr-4" data-testid="landing-closing-booklet-note">
            <BookOpen weight="fill" className="w-4 h-4" />
            {lang === "es"
              ? "Incluye el folleto imprimible gratis de 46 hojas"
              : "Includes the free 46-page printable field-guide booklet"}
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-[#F5A524] hover:bg-[#F5B041] text-[#0A0A0A] font-medium text-base px-6 h-12" data-testid="landing-closing-cta">
              <Camera weight="fill" className="w-5 h-5 mr-2" />
              {c.closing_cta}
            </Button>
          </Link>
          <p className="text-[#52525B] text-xs italic mt-8">{c.footer_note}</p>
        </motion.div>
      </section>
    </div>
  );
}
